import { useState, useEffect, useRef } from 'react';
import { useToast } from '@/hooks/useToast';
import { Input } from '@/components/ui/input';
import { ScryfallCard } from '@/types/scryfall';
import { useAppContext } from '@/hooks/useAppContext';
import { useScryfall } from '@/hooks/useScryfall';
import { Loader2, Plus } from 'lucide-react';

interface QuickAddBarProps {
  onCardAdded: (card: ScryfallCard) => void;
}

export const QuickAddBar = ({ onCardAdded }: QuickAddBarProps) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
  const { toast } = useToast();
  const { config } = useAppContext();
  const scryfall = useScryfall();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      setIsAutocompleteLoading(true);
      try {
        const response = await fetch(`${config.scryfallApiUrl}/cards/autocomplete?q=${encodeURIComponent(query)}`, {
          referrerPolicy: 'no-referrer',
        });
        const data = await response.json();
        if (data.object === 'catalog') {
          setSuggestions(data.data);
        } else {
          setSuggestions([]);
        }
      } catch {
        setSuggestions([]);
      } finally {
        setIsAutocompleteLoading(false);
      }
    };

    const debounceTimeout = setTimeout(fetchSuggestions, 300);

    return () => clearTimeout(debounceTimeout);
  }, [query, config.scryfallApiUrl]);

  const handleAddCard = async (cardName: string, _isFuzzy = false) => {
    if (isAdding) return;
    setIsAdding(true);
    setSuggestions([]);
    setQuery('');

    try {
      // For now, we'll use getCardByName for both exact and fuzzy searches
      // The Scryfall API handles fuzzy matching automatically when using exact parameter
      const card = await scryfall.getCardByName(cardName);

      if (card.object === 'error') {
        throw new Error('Card not found');
      }

      onCardAdded(card);
      toast({
        title: 'Card Added',
        description: `${card.name} has been added to your list.`,
      });

    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && e.shiftKey && query.trim()) {
      e.preventDefault();
      handleAddCard(query, true);
    }
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative">
        <Plus className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Add card by name..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isAdding}
          className="pl-10 h-8"
        />
        {(isAdding || isAutocompleteLoading) && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>
      {suggestions.length > 0 && (
        <div className="absolute z-10 w-full bg-background border border-input rounded-md mt-1 shadow-lg max-h-60 overflow-y-auto">
          <ul>
            {suggestions.map((suggestion) => (
              <li
                key={suggestion}
                className="px-3 py-2 cursor-pointer hover:bg-accent text-sm"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleAddCard(suggestion);
                }}
              >
                {suggestion}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
